"use client";

import { useEffect, useCallback } from "react";
import Link from "next/link";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useRouter } from "next/navigation";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { getDosageIcon, isPlaceholderImage } from "@/lib/dosageIcons";

export function CartSidebar() {
  const {
    items,
    isOpen,
    closeSidebar,
    removeItem,
    updateQuantity,
    totalItems,
    subtotal,
  } = useCart();
  const { formatPrice } = useCurrency();
  const router = useRouter();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-[#0f1419] dark:shadow-black/40 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#e8e8e5] px-6 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-[#111] dark:text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
              />
            </svg>
            <h2 className="text-base font-bold text-[#111] dark:text-white">
              Your Cart{" "}
              {totalItems > 0 && (
                <span className="text-sm font-normal text-[#6b6b6b] dark:text-gray-400">
                  ({totalItems} {totalItems === 1 ? "item" : "items"})
                </span>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeSidebar}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-[#444] transition-colors hover:bg-[#f7f7f5] hover:text-[#111] dark:border-white/10 dark:text-gray-300 dark:hover:bg-[#1e2530] dark:hover:text-white"
            aria-label="Close cart"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#f7f7f5] dark:bg-[#1e2530]">
                <svg
                  className="h-7 w-7 text-[#999]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                  />
                </svg>
              </div>
              <p className="text-sm font-semibold text-[#111] dark:text-white">
                Your cart is empty
              </p>
              <p className="mt-1 text-xs text-[#6b6b6b] dark:text-gray-400">
                Add medications to get started
              </p>
              <button
                type="button"
                onClick={closeSidebar}
                className="mt-6 rounded-full border border-[#111] px-6 py-2.5 text-xs font-semibold text-[#111] transition-colors hover:bg-[#111] hover:text-white dark:border-white/20 dark:text-white dark:hover:bg-white dark:hover:text-[#111]"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const productHref = item.productSlug ? `/medications/${item.productSlug}` : null;
                const handleCardClick = () => {
                  if (!productHref) return;
                  closeSidebar();
                  router.push(productHref);
                };
                const stopNav = (e: React.MouseEvent) => e.stopPropagation();
                return (
                <div
                  key={item.id}
                  onClick={productHref ? handleCardClick : undefined}
                  role={productHref ? "link" : undefined}
                  tabIndex={productHref ? 0 : undefined}
                  onKeyDown={
                    productHref
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick();
                          }
                        }
                      : undefined
                  }
                  className={`flex gap-4 rounded-2xl border border-[#e8e8e5] bg-[#fafaf9] p-4 dark:border-white/10 dark:bg-[#1a1f25] ${
                    productHref ? "cursor-pointer transition-colors hover:border-[#ccc] dark:hover:border-white/20" : ""
                  }`}
                >
                  {/* Product image (or dosage-form fallback when missing) */}
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-linear-to-br from-[#fde8d8] to-[#e8daf5]">
                    {item.image && !isPlaceholderImage(item.image) ? (
                      <img src={item.image} alt={item.name} className="h-full w-full object-contain p-1" />
                    ) : (() => {
                      const DosageIcon = getDosageIcon(item.dosageForm);
                      return <DosageIcon className="h-7 w-7 text-[#b0a8c0] dark:text-gray-500" strokeWidth={1.3} aria-label={item.dosageForm || "Medication"} />;
                    })()}
                  </div>

                  {/* Details */}
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#111] dark:text-white">
                        {item.name}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[#6b6b6b] dark:text-gray-400">
                        {item.countryCode && <CountryFlag code={item.countryCode} size={14} />}
                        <span>{item.variant}</span>
                      </p>
                    </div>
                    <div className="mt-2 flex items-center justify-between" onClick={stopNav}>
                      {/* Quantity controls */}
                      <div className="flex items-center gap-0 rounded-lg border border-[#e0e0dd] dark:border-white/10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            item.quantity > 1
                              ? updateQuantity(item.id, item.quantity - 1)
                              : removeItem(item.id);
                          }}
                          className="flex h-7 w-7 items-center justify-center text-[#666] transition-colors hover:text-[#111] dark:text-gray-400 dark:hover:text-white"
                          aria-label="Decrease quantity"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 12h14"
                            />
                          </svg>
                        </button>
                        <span className="flex h-7 w-7 items-center justify-center border-x border-[#e0e0dd] text-xs font-semibold text-[#111] dark:border-white/10 dark:text-white">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(item.id, item.quantity + 1);
                          }}
                          className="flex h-7 w-7 items-center justify-center text-[#666] transition-colors hover:text-[#111] dark:text-gray-400 dark:hover:text-white"
                          aria-label="Increase quantity"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4.5v15m7.5-7.5h-15"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Price */}
                      <p className="text-sm font-bold text-[#111] dark:text-white">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(item.id);
                    }}
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#999] transition-colors hover:bg-red-50 hover:text-red-500 dark:text-gray-500 dark:hover:bg-red-500/10"
                    aria-label="Remove item"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer / Summary */}
        {items.length > 0 && (
          <div className="border-t border-[#e8e8e5] px-6 py-5 dark:border-white/10">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm text-[#6b6b6b] dark:text-gray-400">Subtotal</span>
              <span className="text-base font-bold text-[#111] dark:text-white">
                {formatPrice(subtotal)}
              </span>
            </div>
            <p className="mb-4 text-[11px] text-[#999] dark:text-gray-500">
              Shipping & taxes calculated at checkout
            </p>
            <button
              type="button"
              onClick={() => {
                closeSidebar();
                router.push("/checkout");
              }}
              className="flex w-full items-center justify-center rounded-2xl bg-[#111] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#333] hover:shadow-lg dark:bg-white dark:text-[#111] dark:hover:bg-gray-200"
            >
              Proceed to Checkout
            </button>
            <Link
              href="/cart"
              onClick={closeSidebar}
              className="mt-3 flex w-full items-center justify-center text-xs font-semibold text-[#111] underline decoration-[#111]/30 underline-offset-2 transition-colors hover:decoration-[#111] dark:text-white dark:decoration-white/30 dark:hover:decoration-white"
            >
              View Full Cart
            </Link>
            <button
              type="button"
              onClick={closeSidebar}
              className="mt-2 w-full text-center text-xs font-medium text-[#6b6b6b] transition-colors hover:text-[#111] dark:text-gray-400 dark:hover:text-white"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
